import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Link,
    Snackbar,
    TextField,
    Typography,
} from '@mui/material';
import { ErrorResponse, errorResponseSchema, reportIssueResponseSchema, reportIssueStatusResponseSchema } from '@zw-app/shared/api';
import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { BACKEND_BASE_URL } from '../constants';
import { db } from '../instant';
import { getConsoleLogs } from '../utils/consoleRingBuffer';
import { blobToRawBase64, captureDomScreenshot, REPORT_ISSUE_EXCLUDE_ATTR } from '../utils/domScreenshot';
import {
    canSubmitReportIssue,
    getReportIssueStatusAlert,
    INITIAL_REPORT_ISSUE_VIEW_STATE,
    reduceReportIssueViewState,
    type ReportIssuePhase,
} from '../utils/reportIssueViewState';

const APP_VERSION = process.env.REACT_APP_GIT_COMMIT ?? 'unknown';

function getPlatform(): string {
    const nav = navigator as Navigator & { userAgentData?: { platform?: string } };
    return nav.userAgentData?.platform ?? navigator.platform ?? '';
}

const DIALOG_EXIT_TIMEOUT_MS = 400;

interface ReportIssueContextValue {
    phase: ReportIssuePhase;
    open: () => void;
}

const ReportIssueContext = createContext<ReportIssueContextValue | null>(null);

export function useReportIssue(): ReportIssueContextValue {
    const ctx = useContext(ReportIssueContext);
    if (!ctx) {
        throw new Error('useReportIssue must be used within ReportIssueProvider');
    }
    return ctx;
}

interface ReportIssueDialogProps {
    open: boolean;
    previewUrl: string | null;
    message: string;
    submitting: boolean;
    statusLoading: boolean;
    statusOk: boolean;
    statusError: string | null;
    errorMessage: string | null;
    onMessageChange: (value: string) => void;
    onTakeScreenshot: () => void;
    onRetake: () => void;
    onSubmit: () => void;
    onClose: () => void;
    onDialogExited: () => void;
}

function ReportIssueDialog(props: ReportIssueDialogProps) {
    const {
        open,
        previewUrl,
        message,
        submitting,
        statusLoading,
        statusOk,
        statusError,
        errorMessage,
        onMessageChange,
        onTakeScreenshot,
        onRetake,
        onSubmit,
        onClose,
        onDialogExited,
    } = props;

    const canSubmit = canSubmitReportIssue({
        message,
        previewUrl,
        statusLoading,
        statusOk,
        submitting,
    });
    const statusAlert = getReportIssueStatusAlert({
        statusLoading,
        statusOk,
        statusError,
    });
    const alertMessage = statusAlert ?? errorMessage;

    return (
        <Dialog
            disableAutoFocus
            disableRestoreFocus
            fullWidth
            maxWidth="sm"
            onClose={onClose}
            open={open}
            slotProps={{ transition: { onExited: onDialogExited } }}
        >
            <DialogTitle>Report issue</DialogTitle>
            <DialogContent>
                <TextField
                    fullWidth
                    label="What went wrong?"
                    margin="normal"
                    minRows={3}
                    multiline
                    onChange={event => onMessageChange(event.target.value)}
                    value={message}
                />
                <Box sx={{ mt: 2 }}>
                    <Typography gutterBottom variant="subtitle2">
                        Screenshot preview
                    </Typography>
                    {previewUrl ? (
                        <Box
                            alt="Screenshot preview"
                            component="img"
                            src={previewUrl}
                            sx={{ maxWidth: '100%', maxHeight: 240, border: '1px solid #e0e0e0', borderRadius: 1 }}
                        />
                    ) : (
                        <Typography color="text.secondary" variant="body2">
                            No screenshot yet — you can still submit your report or take a screenshot below.
                        </Typography>
                    )}
                </Box>
                {alertMessage ? (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {alertMessage}
                    </Alert>
                ) : null}
            </DialogContent>
            <DialogActions>
                {previewUrl ? (
                    <Button disabled={submitting} onClick={onRetake}>
                        Retake
                    </Button>
                ) : (
                    <Button disabled={submitting} onClick={onTakeScreenshot}>
                        Take screenshot
                    </Button>
                )}
                <Button disabled={submitting} onClick={onClose}>
                    Cancel
                </Button>
                <Button disabled={!canSubmit} onClick={onSubmit} variant="contained">
                    Submit
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function CapturingScreenshotOverlay() {
    return (
        <Box
            {...{ [REPORT_ISSUE_EXCLUDE_ATTR]: '' }}
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: theme => theme.zIndex.modal + 1,
                bgcolor: 'rgba(0, 0, 0, 0.45)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
            }}
        >
            <CircularProgress color="inherit" sx={{ color: 'white' }} />
            <Typography color="white" variant="body1">
                Capturing screenshot…
            </Typography>
        </Box>
    );
}

export default function ReportIssueProvider({ children }: { children: React.ReactNode }) {
    const { user } = db.useAuth();
    const [viewState, dispatch] = useReducer(reduceReportIssueViewState, INITIAL_REPORT_ISSUE_VIEW_STATE);
    const [message, setMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [statusLoading, setStatusLoading] = useState(false);
    const [statusOk, setStatusOk] = useState(false);
    const [statusError, setStatusError] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successIssueUrl, setSuccessIssueUrl] = useState<string | null>(null);
    const screenshotBlobRef = useRef<Blob | null>(null);
    const previewUrlRef = useRef<string | null>(null);
    const dialogExitedResolverRef = useRef<(() => void) | null>(null);

    const revokePreviewUrl = useCallback(() => {
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = null;
        }
    }, []);

    const clearStatusState = useCallback(() => {
        setStatusLoading(false);
        setStatusOk(false);
        setStatusError(null);
    }, []);

    const beginStatusLoading = useCallback(() => {
        setStatusLoading(true);
        setStatusOk(false);
        setStatusError(null);
    }, []);

    const runCapture = useCallback(async () => {
        const result = await captureDomScreenshot();
        if (result) {
            revokePreviewUrl();
            previewUrlRef.current = result.objectUrl;
            screenshotBlobRef.current = result.blob;
            dispatch({ type: 'SCREENSHOT_SUCCEEDED', previewUrl: result.objectUrl });
        } else {
            screenshotBlobRef.current = null;
            dispatch({ type: 'SCREENSHOT_FAILED' });
        }
    }, [revokePreviewUrl]);

    const waitForDialogExited = useCallback((): Promise<void> => {
        return new Promise(resolve => {
            const timeoutId = window.setTimeout(() => {
                if (dialogExitedResolverRef.current) {
                    dialogExitedResolverRef.current = null;
                }
                resolve();
            }, DIALOG_EXIT_TIMEOUT_MS);

            dialogExitedResolverRef.current = () => {
                window.clearTimeout(timeoutId);
                dialogExitedResolverRef.current = null;
                resolve();
            };
        });
    }, []);

    const handleDialogExited = useCallback(() => {
        dialogExitedResolverRef.current?.();
    }, []);

    const runScreenshotFlow = useCallback(async () => {
        dispatch({ type: 'START_SCREENSHOT' });
        revokePreviewUrl();
        screenshotBlobRef.current = null;
        await waitForDialogExited();
        await runCapture();
    }, [revokePreviewUrl, runCapture, waitForDialogExited]);

    useEffect(() => {
        if (viewState.phase !== 'dialogOpen' || !user?.refresh_token) {
            return;
        }

        let cancelled = false;
        beginStatusLoading();

        (async () => {
            try {
                const response = await fetch(`${BACKEND_BASE_URL}/report-issue/status`, {
                    method: 'GET',
                    headers: { token: user.refresh_token },
                });
                if (cancelled) {
                    return;
                }

                if (!response.ok) {
                    let parsedError: string | null = null;
                    try {
                        const errorData = (await response.json()) as ErrorResponse;
                        parsedError = errorResponseSchema.validateSync(errorData).error;
                    } catch {
                        parsedError = null;
                    }
                    setStatusOk(false);
                    setStatusError(parsedError);
                    return;
                }

                const resultData = await response.json();
                reportIssueStatusResponseSchema.validateSync(resultData);
                setStatusOk(true);
                setStatusError(null);
            } catch {
                if (!cancelled) {
                    setStatusOk(false);
                    setStatusError(null);
                }
            } finally {
                if (!cancelled) {
                    setStatusLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [beginStatusLoading, viewState.phase, user?.refresh_token]);

    const handleOpen = useCallback(() => {
        if (!user || viewState.phase !== 'idle') {
            return;
        }
        setMessage('');
        setErrorMessage(null);
        beginStatusLoading();
        dispatch({ type: 'OPEN' });
    }, [beginStatusLoading, user, viewState.phase]);

    const handleTakeScreenshot = useCallback(async () => {
        if (viewState.phase !== 'dialogOpen') {
            return;
        }
        await runScreenshotFlow();
    }, [runScreenshotFlow, viewState.phase]);

    const handleRetake = useCallback(async () => {
        if (viewState.phase !== 'dialogOpen') {
            return;
        }
        await runScreenshotFlow();
    }, [runScreenshotFlow, viewState.phase]);

    const handleClose = useCallback(() => {
        revokePreviewUrl();
        screenshotBlobRef.current = null;
        setMessage('');
        setErrorMessage(null);
        clearStatusState();
        dispatch({ type: 'CLOSE' });
    }, [clearStatusState, revokePreviewUrl]);

    const handleSubmit = useCallback(async () => {
        if (!user?.refresh_token || !user.email) {
            setErrorMessage('You must be signed in to report an issue.');
            return;
        }
        if (
            !canSubmitReportIssue({
                message,
                previewUrl: viewState.previewUrl,
                statusLoading,
                statusOk,
                submitting,
            })
        ) {
            return;
        }

        setSubmitting(true);
        setErrorMessage(null);

        try {
            let screenshotPngBase64: string | null = null;
            if (screenshotBlobRef.current) {
                screenshotPngBase64 = await blobToRawBase64(screenshotBlobRef.current);
            }

            const body = {
                message: message.trim(),
                screenshotPngBase64,
                consoleLogs: getConsoleLogs(),
                userAgent: navigator.userAgent,
                platform: getPlatform(),
                viewport: { width: window.innerWidth, height: window.innerHeight },
                url: window.location.href,
                appVersion: APP_VERSION,
                userEmail: user.email,
            };

            const response = await fetch(`${BACKEND_BASE_URL}/report-issue`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    token: user.refresh_token,
                },
                body: JSON.stringify(body),
            });

            if (!response.ok) {
                const errorData = (await response.json()) as ErrorResponse;
                const parsed = errorResponseSchema.validateSync(errorData);
                throw new Error(parsed.error || 'Failed to report issue');
            }

            const resultData = await response.json();
            const result = reportIssueResponseSchema.validateSync(resultData);
            handleClose();
            setSuccessIssueUrl(result.issueUrl);
        } catch (error) {
            const msg = error instanceof Error ? error.message : 'Failed to report issue';
            setErrorMessage(msg);
        } finally {
            setSubmitting(false);
        }
    }, [handleClose, message, statusLoading, statusOk, submitting, user, viewState.previewUrl]);

    const contextValue = useMemo(
        () => ({
            phase: viewState.phase,
            open: handleOpen,
        }),
        [handleOpen, viewState.phase]
    );

    return (
        <ReportIssueContext.Provider value={contextValue}>
            {children}
            {user ? (
                <>
                    <ReportIssueDialog
                        errorMessage={errorMessage}
                        message={message}
                        onClose={handleClose}
                        onDialogExited={handleDialogExited}
                        onMessageChange={setMessage}
                        onRetake={handleRetake}
                        onSubmit={handleSubmit}
                        onTakeScreenshot={handleTakeScreenshot}
                        open={viewState.phase === 'dialogOpen'}
                        previewUrl={viewState.previewUrl}
                        statusError={statusError}
                        statusLoading={statusLoading}
                        statusOk={statusOk}
                        submitting={submitting}
                    />
                    {viewState.phase === 'capturingScreenshot' ? <CapturingScreenshotOverlay /> : null}
                    <Snackbar
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        autoHideDuration={10_000}
                        onClose={() => setSuccessIssueUrl(null)}
                        open={successIssueUrl != null}
                    >
                        <Alert onClose={() => setSuccessIssueUrl(null)} severity="success" sx={{ width: '100%', maxWidth: 560 }}>
                            Issue reported.{' '}
                            {successIssueUrl ? (
                                <Link href={successIssueUrl} rel="noopener noreferrer" target="_blank">
                                    View on GitHub
                                </Link>
                            ) : null}
                        </Alert>
                    </Snackbar>
                </>
            ) : null}
        </ReportIssueContext.Provider>
    );
}
